
import { useState } from 'react';
import { motion } from 'framer-motion';
import { Send, Github, Linkedin, Mail, Phone } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';

const Contact = () => {
  const { toast } = useToast();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    // Simulate form submission
    setTimeout(() => {
      toast({
        title: "Zpráva odeslána",
        description: "Děkujeme za Vaši zprávu. Brzy se Vám ozveme.",
      });
      setFormData({ name: '', email: '', message: '' });
      setIsSubmitting(false);
    }, 1500);
  };

  return (
    <div className="pt-24 min-h-screen">
      <div className="container-section">
        <motion.div 
          className="max-w-6xl mx-auto"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5 }}
        >
          <h1 className="section-title text-center mb-12">Kontakt</h1>
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Contact Info */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="flex flex-col justify-between"
            >
              <div>
                <h2 className="text-2xl font-bold mb-6">Kontaktní informace</h2>
                <p className="text-foreground/80 mb-8">
                  Máte zájem o projekt V3DSTUDIO nebo otázky k jeho využití? Neváhejte mě kontaktovat
                  prostřednictvím formuláře nebo přímo pomocí níže uvedených kontaktů.
                </p>
                
                <div className="space-y-4">
                  <a href="mailto:v3dstudio1@gmail.com" className="flex items-center gap-3 text-foreground/80 hover:text-tech-blue transition-colors">
                    <Mail size={20} />
                    <span>v3dstudio1@gmail.com</span>
                  </a>
                </div>
              </div>
              
              <div className="mt-12">
                <h3 className="text-xl font-bold mb-4">Projekt na sociálních sítích</h3>
                <div className="flex gap-4">
                  <a 
                    href="https://github.com/Vojvyb09" 
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-12 h-12 rounded-full bg-secondary flex items-center justify-center hover:bg-tech-blue/30 transition-colors"
                    aria-label="GitHub"
                  >
                    <Github size={24} className="text-foreground" />
                  </a>
                </div>
                
                <div className="mt-8 p-6 tech-card">
                  <h3 className="text-lg font-bold mb-3">Repozitář projektu</h3>
                  <p className="text-foreground/80 mb-4">
                    Celý projekt včetně zdrojových souborů je dostupný na GitHub repozitáři.
                  </p>
                  <a href="https://github.com/Vojvyb09" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-tech-blue hover:underline">
                    <Github size={18} />
                    <span>github.com/Vojvyb09</span>
                  </a>
                </div>
              </div>
            </motion.div>
            
            {/* Contact Form */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
            >
              <h2 className="text-2xl font-bold mb-6">Napište mi</h2>
              
              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label htmlFor="name" className="block text-foreground mb-2">
                    Jméno
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 bg-secondary border border-tech-charcoal/20 rounded-lg focus:border-tech-blue focus:ring-1 focus:ring-tech-blue outline-none transition-colors"
                  />
                </div>
                
                <div>
                  <label htmlFor="email" className="block text-foreground mb-2">
                    E-mail
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 bg-secondary border border-tech-charcoal/20 rounded-lg focus:border-tech-blue focus:ring-1 focus:ring-tech-blue outline-none transition-colors"
                  />
                </div>
                
                <div>
                  <label htmlFor="message" className="block text-foreground mb-2">
                    Zpráva
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    rows={6}
                    required
                    className="w-full px-4 py-3 bg-secondary border border-tech-charcoal/20 rounded-lg focus:border-tech-blue focus:ring-1 focus:ring-tech-blue outline-none transition-colors resize-none"
                  />
                </div>
                
                <button 
                  type="submit" 
                  disabled={isSubmitting}
                  className={`btn-primary w-full justify-center ${isSubmitting ? 'opacity-70 cursor-not-allowed' : ''}`}
                >
                  {isSubmitting ? 'Odesílám...' : 'Odeslat zprávu'} 
                  <Send size={18} className="ml-2" />
                </button>
              </form>
            </motion.div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default Contact;
